import { PivotItem } from '@fluentui/react';
import { Pivot, pivotContainerStylesFill, pivotStylesFill, TextField, useClassNames } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { ContentsTitle, NameValue } from '../../featurestore/models';
import { EditNameValueList } from '../EditNameValueList/EditNameValueList';
import {
  INameValueViewerClassNames,
  INameValueViewerStyles,
  nameValueViewerStylesDefault,
} from './NameValueViewer.styles';

const convertJsonToString = (json: { [key: string]: string }) => JSON.stringify(json, null, 4);
const getJsonStringAndNameValues = (
  data: { [key: string]: any }[],
  nameField: string,
  valueField?: string,
  dataTypeField?: string
) => {
  const nameValues: NameValue[] = [];
  const json: { [key: string]: string } = {};
  data.forEach((item) => {
    const name = item[nameField];
    const value = valueField ? item[valueField] || '' : '';
    const dataType = dataTypeField ? item[dataTypeField] || '' : '';
    if (name) {
      json[name] = value;
      nameValues.push({ name, dataType, value });
    }
  });
  return { jsonString: convertJsonToString(json), nameValues };
};

interface INameValueViewerProps {
  data?: { [key: string]: any }[];
  nameField?: string;
  dataTypeField?: string;
  valueField?: string;
  editable?: boolean;
  onChange?: (jsonString?: string, nameValues?: NameValue[]) => void;
  onError: (error: unknown, message: string) => void;
}

export const NameValueViewer = ({
  data,
  nameField = 'name',
  valueField = 'value',
  dataTypeField,
  editable,
  onChange,
  onError,
}: INameValueViewerProps) => {
  const [jsonString, setJsonString] = useState<string>('');
  const [nameValues, setNameValues] = useState<NameValue[]>([]);
  const [selectedPivotKey, setSelectedPivotKey] = useState(ContentsTitle.manual);
  useEffect(() => {
    if (data) {
      const { jsonString, nameValues } = getJsonStringAndNameValues(data, nameField, valueField, dataTypeField);
      setJsonString(jsonString);
      setNameValues(nameValues);
      onChange && onChange(jsonString);
    }
  }, [data]);
  const classNames = useClassNames<INameValueViewerStyles, INameValueViewerClassNames>(
    'NameValueViewer',
    nameValueViewerStylesDefault
  );
  return (
    <div className={classNames.root} data-test="nameValueViewer">
      <Pivot
        styles={pivotStylesFill}
        pivotContainerStyles={pivotContainerStylesFill}
        selectedKey={selectedPivotKey}
        onLinkClick={(item?: PivotItem) => {
          try {
            const title = item?.props.itemKey as ContentsTitle;
            const json = JSON.parse(jsonString);
            if (title === ContentsTitle.editor) {
              // sync jsonString with nameValues
              const newNameValues = nameValues.map(({ name = '', dataType }) => {
                return { name, dataType, value: json[name] };
              });
              setNameValues(newNameValues);
            } else {
              nameValues.forEach(({ name = '', value }) => {
                json[name] = value;
              });
              setJsonString(convertJsonToString(json));
            }
            setSelectedPivotKey(title);
          } catch (err) {
            onError(err, `An error has occurred while parsing JSON string: `);
          }
        }}
        items={[
          {
            key: ContentsTitle.manual,
            headerText: editable ? ContentsTitle.manual : ContentsTitle.raw,
            content: (
              <div className={classNames.pivotItem}>
                <TextField
                  readOnly={!editable}
                  styles={{
                    root: { height: '100%' },
                    wrapper: { height: '100%' },
                    fieldGroup: { height: '100%', overflow: 'auto' },
                  }}
                  placeholder="Input JSON"
                  multiline
                  value={jsonString}
                  onChange={(_e, jsonString = '') => {
                    setJsonString(jsonString);
                    onChange && onChange(jsonString);
                  }}
                />
              </div>
            ),
          },
          {
            key: ContentsTitle.editor,
            headerText: editable ? ContentsTitle.editor : ContentsTitle.table,
            content: (
              <div className={classNames.pivotItem}>
                <EditNameValueList
                  editable={editable}
                  showDataType={!!dataTypeField}
                  nameValues={nameValues}
                  onUpdate={(nameValues) => {
                    setNameValues(nameValues);
                    onChange && onChange(undefined, nameValues);
                  }}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
